import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet-async';
import { Box, Breadcrumbs, Container, Grid, Typography, LinearProgress } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { useFlags } from 'launchdarkly-react-client-sdk';
import toast from 'react-hot-toast';

import ChevronRightIcon from '../../template/material-kit-pro/icons/ChevronRight';
import useSettings from '../../template/material-kit-pro/hooks/useSettings';
import gtm from '../../template/material-kit-pro/lib/gtm';
import partnerApi from '../../api';
import useAuth from '../../hooks/useAuth';
import PermissionProvider from '../Permission/PermissionProvider';
import { ROUTES } from '../../routes';
import PipelineStatusSummary from 'src/components/PipelineStatusSummary/PipelineStatusSummary';
import Pipeline from './Pipeline/Pipeline';
import { retrievePersistentDateFilter } from '../../utils/helper';
import { DATE_FILTER_NAMES } from '../../constants/filterNames';

const DealershipLeads: FC = () => {
  const [showStatusSummary, setShowStatusSummary] = useState<boolean>(false);

  const { settings } = useSettings();
  const auth = useAuth();
  const { logout } = auth;

  const { from, to } = retrievePersistentDateFilter(DATE_FILTER_NAMES.DEALERSHIP_LEADS);

  const [displayRange, setDisplayRange] = useState<DateRange<Date>>([DateTime.fromISO(from).toJSDate(), DateTime.fromISO(to).toJSDate()]);

  const setPersistentDisplayRange = (newValue) => {
    localStorage.setItem(`${DATE_FILTER_NAMES.DEALERSHIP_LEADS}.from`, DateTime.fromJSDate(newValue[0]).setLocale('au').toFormat('yyyy-MM-dd'));
    localStorage.setItem(`${DATE_FILTER_NAMES.DEALERSHIP_LEADS}.to`, DateTime.fromJSDate(newValue[1]).setLocale('au').toFormat('yyyy-MM-dd'));
    localStorage.setItem('lastFilterSetDate', DateTime.local().toISODate());
    setDisplayRange(newValue);
  };

  const periodStart = DateTime.fromJSDate(displayRange[0]).setLocale('au');
  const periodEnd = DateTime.fromJSDate(displayRange[1]).setLocale('au').endOf('day');

  const {
    data: leads,
    isLoading,
    error,
    refetch: refetchLeads,
  } = partnerApi(
    'getLeads',
    {
      periodStart: periodStart.toISO(),
      periodEnd: periodEnd.toISO(),
    },
    true
  );

  const { forceUserLogout } = useFlags();

  useEffect(() => {
    async function logoutUser() {
      await logout();
    }

    gtm.push({ event: 'page_view' });

    if (error) {
      if (error.message === 'INSUFFICIENT_SCOPE_ERROR' && forceUserLogout) {
        logoutUser();
      } else {
        toast.error('There was an error when trying retrieve leads', {
          duration: 10000,
        });
        console.error(error);
      }
    }
  }, [error, forceUserLogout]);

  const statusSummaryOnClose = () => setShowStatusSummary(false);
  const statusSummaryBtnOnClick = () => setShowStatusSummary(!showStatusSummary);

  return (
    <PermissionProvider routeName={ROUTES.LEADS}>
      <Helmet>
        <title>Group Leads - Driva Dashboard</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 8,
          display: 'flex',
        }}
      >
        <Container maxWidth={settings.compact ? 'xl' : false} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Grid container justifyContent="space-between" spacing={3}>
            <Grid item>
              <Typography color="textPrimary" variant="h5">
                Group Leads
              </Typography>
              <Breadcrumbs aria-label="breadcrumb" separator={<ChevronRightIcon fontSize="small" />} sx={{ mt: 1 }}>
                <Typography color="textSecondary" variant="subtitle2">
                  Listed are all group leads sent to Driva for finance quotes. Go to my leads to see the leads you sent.
                </Typography>
              </Breadcrumbs>
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexGrow: 1,
              flexDirection: 'column',
            }}
          >
            {isLoading && <LinearProgress sx={{ mt: 3, mb: 3 }} />}
            <Pipeline leads={leads} displayRange={displayRange} setDisplayRange={setPersistentDisplayRange} refetchLeads={refetchLeads} />
          </Box>
        </Container>
      </Box>
      <PipelineStatusSummary isOpen={showStatusSummary} onClose={statusSummaryOnClose} />
      <PipelineStatusSummary.Button onClick={statusSummaryBtnOnClick} />
    </PermissionProvider>
  );
};

export default DealershipLeads;
